import Storage from './LocalStorage';

export type LandingPageScenarioType = 'default' | 'scenario_b';

interface LandingPageScenario {
  Default: 'default';
  ScenarioB: 'scenario_b';
}

interface TestingScenarios {
  LandingPage: LandingPageScenario;
}

/**
 * Contains the keys for A/B testing scenarios.
 */
export const ABTestingKeys = {
  LandingPage: 'landingPage.scenario'
} as const;

/**
 * Defines the possible values for the A/B testing scenarios, specifically for the LandingPage scenario.
 */
export const ABTestingScenarios: TestingScenarios = {
  LandingPage: {
    Default: 'default',
    ScenarioB: 'scenario_b'
  }
};

/**
 * Represents the overall A/B testing configuration, specifically for the LandingPage scenario.
 */
export type ABTesting = {
  LandingPage: {
    scenario: string;
  };
};

/**
 * The initial A/B testing configuration with default values.
 */
export const initialABTesting: ABTesting = {
  LandingPage: { scenario: ABTestingScenarios.LandingPage.Default }
};

/**
 * Sets the value for the LandingPage A/B testing scenario in the local storage.
 *
 * @param {string} value - The value to be set for the LandingPage scenario.
 */
function setLandingPageScenario(value: string) {
  Storage.setABTestingValue(ABTestingKeys.LandingPage, value);
}

/**
 * Retrieves the A/B testing configuration object from local storage.
 *
 * @returns {ABTesting} - The A/B testing configuration object.
 */
function getObject(): ABTesting {
  return {
    LandingPage: {
      scenario: Storage.getABTestingValue(ABTestingKeys.LandingPage)
    }
  };
}

/**
 * Retrieves the value for a specific A/B testing scenario key from the local storage.
 *
 * @param {string} scenarioKey - The key of the A/B testing scenario to retrieve.
 * @returns {string} - The value of the specified A/B testing scenario.
 */
function getScenario(scenarioKey: (typeof ABTestingKeys)[keyof typeof ABTestingKeys]) {
  return Storage.getABTestingValue(scenarioKey);
}

/**
 * Sets the LandingPage scenario to its default value in the local storage.
 */
function setABTestingDefault() {
  setLandingPageScenario(initialABTesting.LandingPage.scenario);
}

/**
 * Checks if any A/B testing scenario is unset or missing in the local storage.
 *
 * @returns {boolean} - True if any scenario is unset or missing; otherwise, false.
 */
function isAnyScenarioUnset() {
  const object = getObject();

  return ![object.LandingPage.scenario].reduce(
    (isUnset, currentValue) => isUnset && !!currentValue,
    true
  );
}

/**
 * Initializes A/B testing scenarios, possibly with the option to exclude A/B testing.
 *
 * @param {boolean} noAB - If true, A/B testing will be excluded; otherwise, it will be included.
 */
function initScenarios(noAB = false) {
  if (noAB) setABTestingDefault();

  decideABTestingScenarios(noAB);
}

/**
 * Decides and sets A/B testing scenarios if any scenario is unset.
 *
 * @param {boolean} noAB - If true, A/B testing will be excluded; otherwise, it will be included.
 */
function decideABTestingScenarios(noAB) {
  if (isAnyScenarioUnset()) {
    // Decide the A/B testing scenario for the landing page
    const landingPageScenario =
      Math.random() > 0.5
        ? ABTestingScenarios.LandingPage.Default
        : ABTestingScenarios.LandingPage.ScenarioB;

    // Set the A/B testing scenario for the landing page
    setLandingPageScenario(landingPageScenario);
  }
}

/**
 * A collection of functions related to A/B testing operations.
 */
export const ABTest = {
  setLandingPageScenario: setLandingPageScenario,
  getObject: getObject,
  getScenario: getScenario,
  initScenarios: initScenarios
};
